<template>
    <a-modal
        class="o-add-resource"
        name="add-resource"
        :resizable="true"
        :height="650"
        :minWidth="600"
        :minHeight="650"
        @onClose="onModalCloseHandler"
    >
            <h2>
                {{ $t('app.AddResource') }}
            </h2>

            <ValidationObserver
                ref="addResource"
                tag="form"
                class="modal-form"
                @submit.prevent="addResource"
            >
                <div class="columns-holder">
                    <span class="field-label">{{ $t('app.ResourceKind') }}</span>
                    <RadioGroup class="field resource-kind" v-model="resourceKind" @on-change="resourceChange">
                        <Radio :label="resourceKindString">
                            <span>{{ $t('app.SimpleText') }}</span>
                        </Radio>
                        <Radio :label="resourceKindRichtext">
                            <span>{{ $t('app.HtmlDocument') }}</span>
                        </Radio>
                        <Radio :label="resourceKindGoogledoc">
                            <span>{{ $t('app.GoogleDocument') }}</span>
                        </Radio>
                    </RadioGroup>
                    
                    <template v-if="resourceKind == resourceKindGoogledoc">
                        <a-input
                            v-model="googleDocUrl"
                            :validation="true"
                            :validation-config="{
                                name: 'googleDocUrl',
                                rules: {
                                    required: true,
                                },
                            }"
                            class="app-row-form field"
                            :label="$t('app.googleDocUrl')"
                            :placeholder="$t('app.EnterGoogleDocUrl')"
                        />
                    </template>
                    <template v-else>
                        <a-input
                            v-model="resourceKey"
                            :validation="true"
                            :validation-config="{
                                name: 'resourceKey',
                                rules: {
                                    required: true,
                                },
                            }"
                            class="app-row-form field"
                            :label="$t('app.Key')"
                            :placeholder="$t('app.EnterResourceKey')"
                        />
                    </template>    

                    <span class="field-label">{{ $t('app.PrimaryLanguage') }}</span>
                    <Select class="field" v-model="langCode">
                        <Option 
                            v-for="lang in languages" 
                            :key="lang.code" 
                            :value="lang.code">
                            {{ lang.name }} ({{ lang.code }})
                        </Option>
                    </Select>

                    <template v-if="resourceKind != resourceKindGoogledoc">
                        <span class="field-label">{{ $t('app.Value') }}</span>
                        <ValidationProvider
                            v-slot="{ errors }"
                            name="resourceValue"
                            :rules="{
                                required: true,
                            }"
                            mode="eager"
                            class="modal-form-textarea"
                        >
                            <div class="field">
                                <template v-if="resourceKind == resourceKindString">
                                    <b-form-textarea
                                        v-model="resourceValue"
                                        rows="6"
                                        max-rows="6"
                                    />
                                </template>
                                <template v-else>
                                    <ckeditor @ready="onEditorLoadReady" class="editor-add-plagin-hidden"/>
                                    <b-spinner v-if="!isEditorAddPlagin" />
                                    <ckeditor
                                        v-if="isEditorAddPlagin"
                                        v-model="resourceValue"
                                        :config="editorConfig"
                                        class="popup-editor__value"
                                        @ready="onEditorReady"
                                    />
                                </template>    
                                <span class="app-error">{{ errors[0] }}</span>
                            </div>
                        </ValidationProvider>
                    </template>

                    <span class="field-label">{{ $t('app.Tags') }}</span>
                    <Select
                        class="field"
                        v-model="selectedTags"
                        multiple
                        filterable
                        allow-create
                        @on-create="handleCreateTag"
                    >
                        <Option
                            v-for="(tag, index) in tags"
                            :key="index"
                            :value="tag"
                            >{{ tag }}</Option
                        >
                    </Select>

                    <span class="field-label">{{ $t('app.ResourceDescription') }}</span>
                    <a-textarea
                        v-model="resourceDescription"
                        rows="2"
                        max-rows="2"
                        :validation="true"
                        :validation-config="{
                            name: 'resourceDescription',
                            rules: {
                                max: 1000,
                            },
                        }"
                    />
                </div>

                <div class="btns-holder">
                    <Button 
                        :disabled="uploadFileInProgress"
                        :loading="isSavingInProgress"
                        type="primary" 
                        html-type="submit"
                    >
                        {{ $t('app.Add') }}
                    </Button>
                </div>
            </ValidationObserver>
    </a-modal>
</template>

<script>
import { Button, Select, Option, AutoComplete, RadioGroup, Radio } from 'view-design'
import AModal from '@atoms/a-modal'
import AAutoComplete from '@atoms/a-auto-complete'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import AInput from '@atoms/a-input'
import _ from 'lodash'
import { delay } from '@utils/helpers'
import { Role } from '@/constants/role.js'
import { ResourceKind } from '@/constants/resource-kind.js'
import { ChangingRoleForOwnerError, OwnerAlreadyExistsError } from '@utils/errors'
import ATextarea from '@atoms/a-textarea'
import CkImageUpload from '@libs/ck-image-upload/index'
import CkImageUploadCss from '@libs/ck-image-upload/css'
import { oembedReplace } from '@utils/oembed/'

export default {
    name: 'OAddResource',
    components: {
        AutoComplete,
        AAutoComplete,
        AModal,
        Button,
        ValidationObserver,
        ValidationProvider,
        AInput,
        Select, 
        Option,
        RadioGroup, 
        Radio,
        ATextarea,
    },
    props: {
        onAddResource: {
            type: Function,
            required: true,
        },
        onAddGoogleDoc: {
            type: Function,
            required: true,
        },
        onUploadFile: {
            type: Function,
            required: true
        },
        languages: {
            type: Array,
            required: true,
        },
        onGetDefaultLangCode: {
            type: Function
        },
        tags: {
            type: Array,
            required: true,
        },
    },
    watch: {
    },
    data() {
        return {
            uploadFileInProgress: false,
            resourceKind: ResourceKind.string,
            resourceKey: '',
            googleDocUrl: '',
            resourceDescription: '',
            isSavingInProgress: false,
            selectedTags: [],
            resourceValue: "",
            langCode: "",
            isEditorAddPlagin: false,
            editorConfig: {},
        }
    },
    mounted() {
        
    },
    computed: {
        resourceKindString() {
            return ResourceKind.string;
        },
        resourceKindRichtext() {
            return ResourceKind.richtext;
        },
        resourceKindGoogledoc() {
            return ResourceKind.googledoc;
        },
    },
    methods: {
        resetData() {
            this.resourceKind = ResourceKind.string;
            this.resourceKey = '';
            this.googleDocUrl = '';
            this.resourceDescription = '';
            this.isSavingInProgress = false;
            this.selectedTags = [];
            this.resourceValue = "";
            this.langCode = "";
        },
        onModalCloseHandler() {
            this.$modal.hide('add-resource');
        },
        show() {
            this.resetData();
            let lCode = this.onGetDefaultLangCode();
            if (lCode.trim() === '') {
                lCode = this.languages[0].code;
            }
            this.langCode = lCode;
            this.$modal.show('add-resource');
        },
        async handleCreateTag(tag) {
            this.selectedTags.push(tag)
        },
        async addResource() {
            const isValid = await this.$refs.addResource.validate()
            if (!isValid) return

            try {
                this.isSavingInProgress = true;
                
                if (this.resourceKind == this.resourceKindGoogledoc) {
                    try {
                        await this.onAddGoogleDoc({
                            langCode: this.langCode,
                            url: this.googleDocUrl,
                            description: this.resourceDescription,
                            tags: this.selectedTags,
                        })
                    } catch (err) {
                        if (err.message == 'WrongUrl') {
                            this.$refs.addResource.setErrors({
                                googleDocUrl: [this.$t('app.WrongGoogleDocUrl')]
                            })
                            this.isSavingInProgress = false;
                            return;    
                        } else {
                            throw err;
                        }
                    }
                } else {
                    await this.onAddResource({
                        langCode: this.langCode,
                        key: this.resourceKey,
                        value: this.resourceValue,
                        description: this.resourceDescription,
                        tags: this.selectedTags,
                        kind: this.resourceKind,
                    });
                }

                this.isSavingInProgress = false;
                this.resetData();

                this.$modal.hide('add-resource');
            } catch (err) {
                this.isSavingInProgress = false;
                throw err;
            }
        },
        onEditorLoadReady() {
            if(window.CKEDITOR) {
                CkImageUpload(async file => {
                    let res = ''
                    if (this.onUploadFile) {
                        res = await this.onUploadFile(file)
                    }
                    return res
                });
                setTimeout(() => {
                    this.editorConfig = {
                        allowedContent: true,
                        language: this.$i18n.locale,
                        extraPlugins: 'embed,autoembed,image2,apiimage',
                        removePlugins: 'image',
                        removeButtons: 'Cut,Copy,Paste,PasteText,PasteFromWord,About,Maximize,HorizontalRule,SpecialChar,Subscript,Superscript,Scayt',
                        embed_provider: '//ckeditor.iframe.ly/api/oembed?url={url}&callback={callback}',
                        contentsCss: CkImageUploadCss,
                    };
                    this.isEditorAddPlagin = true;
                }, 0);
            }
        },
        onEditorReady(editor) {
            if (this.resourceValue && this.resourceValue !== '') {
                this.resourceValue = oembedReplace(this.resourceValue)
            }
            editor.on('mode', () => {
                this.uploadFileInProgress = editor.mode === 'source'
            });
        },
        resourceChange() {
            this.resourceValue = '';
            this.uploadFileInProgress = false
        }
    },
}
</script>

<style lang="scss">
@import 'theme';
</style>
